import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useEffect, useState } from "react";
import { DateRangePicker } from "./date-picker/date-picker-menu";

export interface RedoDateRangePickerDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  style?: React.CSSProperties;
  popperModifiers?: any[];
}

export const RedoDateRangePickerDropdown = memo(
  function RedoDateRangePickerDropdown({
    anchorEl,
    open,
    onClose,
    dateRange,
    setDateRange,
    minDate,
    maxDate,
    allowNoDateOption = false,
    style,
    popperModifiers,
  }: RedoDateRangePickerDropdownProps) {
    const [localStartDate, setLocalStartDate] =
      useState<Temporal.PlainDate | null>(null);

    const [localEndDate, setLocalEndDate] = useState<Temporal.PlainDate | null>(
      null,
    );

    const handleSetCustomDate = useHandler(
      (start: Temporal.PlainDate | null, end: Temporal.PlainDate | null) => {
        setLocalStartDate(start);
        setLocalEndDate(end);
      },
    );

    useEffect(() => {
      setLocalStartDate(dateRange ? dateRange[0] : null);
      setLocalEndDate(dateRange ? dateRange[1] : null);
    }, [dateRange]);

    const handleApply = useHandler(() => {
      if (localStartDate && localEndDate) {
        const newArray: [Temporal.PlainDate, Temporal.PlainDate] = [
          localStartDate,
          localEndDate,
        ];

        setDateRange(newArray);
      } else {
        setDateRange(undefined);
      }
      onClose();
    });

    const defaultModifiers = [
      { name: "offset", options: { offset: [0, 8] } },
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "flip", options: { altBoundary: true } },
    ];

    return (
      <ClickAwayListener onClickAway={onClose}>
        <Popper
          anchorEl={anchorEl}
          modifiers={popperModifiers ?? defaultModifiers}
          open={open}
          placement="bottom-start"
          style={style}
        >
          <DateRangePicker
            allowNoDateOption={allowNoDateOption}
            endDate={localEndDate}
            maxDate={maxDate}
            minDate={minDate}
            onApply={handleApply}
            onCancel={onClose}
            onRangeChange={handleSetCustomDate}
            startDate={localStartDate}
            variant="range"
          />
        </Popper>
      </ClickAwayListener>
    );
  },
);
